import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, mergeMap, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ReduxApiHandlerService } from '../service/state-api-handler.service';
import { ApiCacheService } from '../service/api-cache.service';
import {
  callApi,
  callApiSuccess,
  callApiFailure,
  clearDedupApiCache,
  loadActivatedEventsData,
  loadActivatedEventsDataSuccess,
  loadActivatedEventsDataFailure
} from './action';
import { EventPerformerSummaryService } from '../service/event-performer-summary.service';
import { formatDate } from '@angular/common';

/**
 * Handles API-related side effects for the application, including generic API calls and loading activated event summary data.
 * @component
 * @selector undefined
 * @example
 *   <api-effects></api-effects>
 * @input N/A - This injectable effect class does not expose input properties.
 * @output N/A - This injectable effect class does not emit output events.
 * @method callApi$ - Listens for callApi actions, invokes the configured API endpoint, transforms the response into a normalized data shape, and dispatches success or failure actions.
 * @method loadActivatedEventsData$ - Listens for loadActivatedEventsData actions, retrieves activated event data, formats and enriches the results, and dispatches success or failure actions.
 * @lifecycle NgRx effect lifecycle - Runs when matching actions are dispatched; no Angular component lifecycle hooks are used.
 */
@Injectable()
export class ApiEffects {
  private actions$ = inject(Actions);
  private api = inject(ReduxApiHandlerService);
  private eventPerformerSummary = inject(EventPerformerSummaryService);
  private apiCache = inject(ApiCacheService);

  /**
   * Wipes the interceptor-level dedup response cache when clearDedupApiCache
   * is dispatched (company switch). Non-dispatching: cache invalidation is a
   * terminal side effect with no follow-up action.
   */
  clearDedupApiCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(clearDedupApiCache),
        tap(() => this.apiCache.clearAll()),
        // Effects rule: an unhandled error kills the stream permanently.
        // clearAll() is synchronous in-memory work and should never throw,
        // but if it does, resubscribe so later dispatches still clear the cache.
        catchError((_err: unknown, caught) => caught)
      ),
    { dispatch: false }
  );

  callApi$ = createEffect(() =>
    this.actions$.pipe(
      ofType(callApi),
      mergeMap(({ method, endpoint, payload, key }) =>
        this.api.callApi(method, endpoint, payload).pipe(
          map((res: any) => {
            const parsedData = Array.isArray(res.body)
              ? { data: res.body, total: res.body.length }
              : res.body?.data
              ? { data: res.body.data, total: res.body.total || res.body.data.length }
              : { data: res.body, total: 0 };
            return callApiSuccess({ key: key || endpoint, data: parsedData });
          }),
          catchError((error) => of(callApiFailure({ key: key || endpoint, error })))
        )
      )
    )
  );

  loadActivatedEventsData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadActivatedEventsData),
      mergeMap(({ params }) =>
        this.eventPerformerSummary.getActivatedEventData(params).pipe(
          map((response) => {
            if (response.status === 200) {
              const formattedData = response.body.result.map((item: any, index: number) => ({
                ...item,
                Sr: index + 1,
                natureOfEvent: item.natureOfEvent ?? '',
                activationDate: item.activationDate ? item.activationDate : null,
                due_date: (item.due_date && item.due_date !== '0001-01-01T00:00:00')
                  ? formatDate(item.due_date, 'dd-MM-yyyy', 'en-US')
                  : '',
                held_on: (item.held_on && item.held_on !== '0001-01-01T00:00:00')
                  ? formatDate(item.held_on, 'dd-MM-yyyy', 'en-US')
                  : ''
              }));
              return loadActivatedEventsDataSuccess({ data: formattedData });
            } else {
              return loadActivatedEventsDataSuccess({ data: [] });
            }
          }),
          catchError((error) => of(loadActivatedEventsDataFailure({ error })))
        )
      )
    )
  );
}